import React, { useRef, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Lock, Unlock, Coffee, Moon, Settings, Clock } from 'lucide-react';
import type { LockMode } from '../../store/disciplineStore';
import { addToast } from '../../store/toastStore';

interface TradingLockButtonProps {
  isDisciplineLocked: boolean;
  lockMode: LockMode;
  lockExpiresAt: number | null;
  lockDropdownOpen: boolean;
  setLockDropdownOpen: (open: boolean) => void;
  startCoolOff: (minutes: number) => void;
  startCoolOffUntil: (untilTimestamp: number) => void;
  setDoneForDay: () => void;
  openUnlockModal: () => void;
  openSettingsModal: () => void;
  simpleUnlock: () => Promise<{ success: boolean; error?: string }>;
  simpleUnlockEnabled: boolean;
  activeBroker: any;
}

export const TradingLockButton: React.FC<TradingLockButtonProps> = ({
  isDisciplineLocked,
  lockMode,
  lockExpiresAt,
  lockDropdownOpen,
  setLockDropdownOpen,
  startCoolOff,
  startCoolOffUntil,
  setDoneForDay,
  openUnlockModal,
  openSettingsModal,
  simpleUnlock,
  simpleUnlockEnabled,
  activeBroker,
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [showCustomTime, setShowCustomTime] = useState(false);
  const [customMinutes, setCustomMinutes] = useState(60); // Default 60 minutes

  // Calculate dropdown position with viewport constraints for mobile
  useEffect(() => {
    if (!lockDropdownOpen || !buttonRef.current) return;

    const calculatePosition = () => {
      const rect = buttonRef.current!.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const dropdownWidth = 170;

      // Get actual dropdown height after render, fallback to estimate
      const dropdownHeight = dropdownRef.current?.scrollHeight || 350;

      // Calculate initial position (centered below button)
      let top = rect.bottom + 6;
      let left = rect.left + rect.width / 2;

      // Check if dropdown fits below
      const fitsBelow = top + dropdownHeight <= viewportHeight - 10;
      // Check if dropdown fits above
      const fitsAbove = rect.top - dropdownHeight - 6 >= 10;

      if (!fitsBelow) {
        if (fitsAbove) {
          // Show above the button
          top = rect.top - dropdownHeight - 6;
        } else {
          // Doesn't fit above or below - position at top of screen
          top = 10;
        }
      }

      // Constrain horizontal position to keep dropdown on screen
      const halfWidth = dropdownWidth / 2;
      if (left - halfWidth < 10) {
        left = halfWidth + 10;
      } else if (left + halfWidth > viewportWidth - 10) {
        left = viewportWidth - halfWidth - 10;
      }

      setDropdownPosition({ top, left });
    };

    // Initial calculation
    calculatePosition();

    // Recalculate after a frame to get actual dropdown dimensions
    requestAnimationFrame(calculatePosition);
  }, [lockDropdownOpen]);

  // Close on outside click
  useEffect(() => {
    if (!lockDropdownOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setLockDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [lockDropdownOpen, setLockDropdownOpen]);

  if (!activeBroker) return null;

  const formatTimeRemaining = (expiresAt: number): string => {
    const remaining = expiresAt - Date.now();
    if (remaining <= 0) return 'now';

    const totalSeconds = Math.ceil(remaining / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  };

  const handleClick = async () => {
    if (isDisciplineLocked) {
      // cool_off and done_for_day are timer-based - show remaining time
      if (lockMode === 'cool_off' || lockMode === 'done_for_day') {
        if (lockExpiresAt) {
          const timeStr = formatTimeRemaining(lockExpiresAt);
          const modeStr = lockMode === 'cool_off' ? 'Cool-off' : 'Done for day';
          addToast('info', `${modeStr} ends in ${timeStr}`, 4000);
        } else {
          addToast('info', 'Trading locked - waiting for timer', 3000);
        }
        return;
      }
      // Other locks can use simple unlock
      if (simpleUnlockEnabled) {
        await simpleUnlock();
      } else {
        openUnlockModal();
      }
    } else {
      setLockDropdownOpen(!lockDropdownOpen);
    }
  };

  return (
    <div className="trading-lock-wrapper">
      <button
        ref={buttonRef}
        className={`trading-lock-btn ${isDisciplineLocked ? 'locked' : 'unlocked'}`}
        onClick={handleClick}
        title={isDisciplineLocked ? 'Trading Locked - Click to unlock' : 'Trading Unlocked - Click to lock'}
      >
        {isDisciplineLocked ? <Lock size={12} /> : <Unlock size={12} />}
      </button>

      {lockDropdownOpen && !isDisciplineLocked && createPortal(
        <>
          <div className="trading-popover-overlay" onClick={() => setLockDropdownOpen(false)} />
          <div
            ref={dropdownRef}
            className="trading-lock-dropdown"
            style={{
              position: 'fixed',
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              transform: 'translateX(-50%)',
            }}
          >
            <button onClick={() => { startCoolOff(2); setLockDropdownOpen(false); }}>
              <Coffee size={11} /> Cool Off (2 min)
            </button>
            <button onClick={() => { startCoolOff(5); setLockDropdownOpen(false); }}>
              <Coffee size={11} /> Cool Off (5 min)
            </button>
            <button onClick={() => { startCoolOff(10); setLockDropdownOpen(false); }}>
              <Coffee size={11} /> Cool Off (10 min)
            </button>
            <button onClick={() => { startCoolOff(30); setLockDropdownOpen(false); }}>
              <Coffee size={11} /> Cool Off (30 min)
            </button>
            {!showCustomTime ? (
              <button onClick={() => setShowCustomTime(true)}>
                <Clock size={11} /> Custom Time...
              </button>
            ) : (
              <div className="trading-lock-custom-time">
                <select
                  value={customMinutes}
                  onChange={(e) => setCustomMinutes(Number(e.target.value))}
                  autoFocus
                >
                  {/* 10-minute intervals from 10 mins to 12 hours (720 mins) */}
                  {Array.from({ length: 72 }, (_, i) => (i + 1) * 10).map(mins => (
                    <option key={mins} value={mins}>
                      {mins < 60 ? `${mins} min` : mins % 60 === 0 ? `${mins / 60} hr` : `${Math.floor(mins / 60)}h ${mins % 60}m`}
                    </option>
                  ))}
                </select>
                <button
                  onClick={() => {
                    startCoolOff(customMinutes);
                    setLockDropdownOpen(false);
                    setShowCustomTime(false);
                    setCustomMinutes(60);
                  }}
                >
                  Set
                </button>
              </div>
            )}
            <div className="trading-lock-dropdown-divider" />
            <button className="done-for-day" onClick={() => { setDoneForDay(); setLockDropdownOpen(false); }}>
              <Moon size={11} /> Done for Day
            </button>
            <div className="trading-lock-dropdown-divider" />
            <button onClick={() => { openSettingsModal(); setLockDropdownOpen(false); }}>
              <Settings size={11} /> Settings
            </button>
          </div>
        </>,
        document.body
      )}
    </div>
  );
};
